import { FormGroup, FormControl, Validators } from '@angular/forms';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { REGEX_CONSTANT } from '@sharedModule/constants/pattern-regex.constant';
import { VelocityCheckPayload } from '@sharedModule/models/delivery.model';
import { getDeliveryDetailsControls } from '@sharedModule/utils/transfer-details.utils';
import { TransactionChargesInterface } from '@feature/dashboard/transactions/types/create-transaction';

/** Sender sub-form, including the inline new-sender (KYC) fields. Password is
 * always required for a new sender; personal / document fields follow the
 * ONLINE-mode flag. */
const getSenderDetailsGroup = (isRequired: boolean): FormGroup => {
  const attr = ATTRIBUTES_CONSTANT.transactions;
  const required = isRequired ? [Validators.required] : [];
  return new FormGroup({
    [attr.firstName.key]: new FormControl('', [Validators.required]),
    [attr.middleName.key]: new FormControl(''),
    [attr.lastName.key]: new FormControl('', [Validators.required]),
    [attr.address1.key]: new FormControl('', [Validators.required]),
    [attr.city.key]: new FormControl('', [Validators.required]),
    [attr.country.key]: new FormControl('', [Validators.required]),
    [attr.postalCode.key]: new FormControl('', [Validators.required]),
    [attr.email.key]: new FormControl('', [
      Validators.required,
      Validators.pattern(REGEX_CONSTANT.email),
    ]),
    [attr.mobileCode.key]: new FormControl({ value: '', disabled: true }),
    [attr.mobileNumber.key]: new FormControl('', [
      Validators.required,
      Validators.pattern(REGEX_CONSTANT.mobileNumber),
    ]),
    [attr.password.key]: new FormControl('', [Validators.required]),
    [attr.gender.key]: new FormControl('', required),
    [attr.dob.key]: new FormControl('', required),
    [attr.pin.key]: new FormControl(''),
    [attr.address2.key]: new FormControl(''),
    [attr.town.key]: new FormControl(''),
    [attr.docType.key]: new FormControl('', required),
    [attr.docSection.key]: new FormControl('', required),
    [attr.docNumber.key]: new FormControl('', required),
    [attr.docExpiryDate.key]: new FormControl('', required),
    [attr.image.key]: new FormControl('', required),
  });
};

/** Beneficiary sub-form, including the conditional delivery-detail controls. */
const getBeneDetailsGroup = (): FormGroup => {
  const attr = ATTRIBUTES_CONSTANT.transactions;
  return new FormGroup({
    [attr.beneFirstName.key]: new FormControl('', [Validators.required]),
    [attr.beneMiddleName.key]: new FormControl(''),
    [attr.beneLastName.key]: new FormControl('', [Validators.required]),
    [attr.beneAddress.key]: new FormControl('', [Validators.required]),
    [attr.beneCity.key]: new FormControl('', [Validators.required]),
    [attr.beneCountry.key]: new FormControl('', [Validators.required]),
    [attr.benePostCode.key]: new FormControl('', [Validators.required]),
    [attr.beneEmail.key]: new FormControl('', [
      Validators.required,
      Validators.pattern(REGEX_CONSTANT.email),
    ]),
    [attr.beneMobileCode.key]: new FormControl({ value: '', disabled: true }),
    [attr.beneMobileNumber.key]: new FormControl('', [
      Validators.required,
      Validators.pattern(REGEX_CONSTANT.mobileNumber),
    ]),
    // Validators toggled by app-transfer-details based on transfer_type_po.
    ...getDeliveryDetailsControls(),
  });
};

export const getCreateTransactionForm = (isRequired: boolean): FormGroup => {
  const attr = ATTRIBUTES_CONSTANT.transactions;
  const onlineRequired = isRequired ? [Validators.required] : [];
  return new FormGroup({
    [attr.sourceCountry.key]: new FormControl('', [Validators.required]),
    [attr.destCountry.key]: new FormControl('', [Validators.required]),
    [attr.transferType.key]: new FormControl('', [Validators.required]),
    [attr.serviceLevel.key]: new FormControl('', [Validators.required]),
    [attr.sourceOfIncome.key]: new FormControl('', onlineRequired),
    [attr.purpose.key]: new FormControl('', onlineRequired),
    [attr.paymentType.key]: new FormControl('', [Validators.required]),
    senderDetails: getSenderDetailsGroup(isRequired),
    beneDetails: getBeneDetailsGroup(),
    [attr.sourceCurrency.key]: new FormControl({ value: '', disabled: true }),
    [attr.sourceAmount.key]: new FormControl('', [Validators.required]),
    [attr.destCurrency.key]: new FormControl({ value: '', disabled: true }),
    [attr.destAmount.key]: new FormControl('', [Validators.required]),
    [attr.headOfficeFee.key]: new FormControl('', [Validators.required]),
    [attr.commission.key]: new FormControl('', [Validators.required]),
    [attr.rate.key]: new FormControl('', [Validators.required]),
  });
};

/** Strips the calculated / nested controls that are added back explicitly when
 * building the createSenderTransaction payload. Non-mutating. */
export const stripCalculatedFields = (
  raw: Record<string, unknown>
): Record<string, unknown> => {
  const attr = ATTRIBUTES_CONSTANT.transactions;
  const result = { ...raw };
  [
    'senderDetails',
    'beneDetails',
    attr.sourceAmount.key,
    attr.destAmount.key,
    attr.headOfficeFee.key,
    attr.commission.key,
    attr.rate.key,
  ].forEach(key => delete result[key]);
  return result;
};

/** Builds the getCharges payload from the current form + chosen amount type. */
export const buildChargesPayload = (
  form: FormGroup,
  amountType: string
): TransactionChargesInterface => {
  const attr = ATTRIBUTES_CONSTANT.transactions;
  const raw = form.getRawValue();
  return {
    src_country: raw[attr.sourceCountry.key],
    dest_country: raw[attr.destCountry.key],
    source_currency: raw[attr.sourceCurrency.key],
    destination_currency: raw[attr.destCurrency.key],
    action: amountType,
    amount:
      amountType === 'source'
        ? Number(raw[attr.sourceAmount.key])
        : Number(raw[attr.destAmount.key]),
    transfer_type_po: raw[attr.transferType.key],
    service_level: raw[attr.serviceLevel.key],
    payment_type: raw[attr.paymentType.key],
  };
};

/** Builds the checkVelocityLimit payload from the current form. */
export const buildVelocityPayload = (form: FormGroup): VelocityCheckPayload => {
  const attr = ATTRIBUTES_CONSTANT.transactions;
  const raw = form.getRawValue();
  return {
    transfer_type_po: raw[attr.transferType.key],
    source_currency: raw[attr.sourceCurrency.key],
    destination_currency: raw[attr.destCurrency.key],
    dest_country: raw[attr.destCountry.key],
    receive_amount: Number(raw[attr.destAmount.key]),
  };
};

/** Converts the new-sender date controls to the DD-MM-YYYY backend format. */
export const buildNewSenderPayload = (
  senderDetails: Record<string, unknown>,
  formatDate: (value: string) => string
): Record<string, unknown> => {
  const attr = ATTRIBUTES_CONSTANT.transactions;
  const dobKey = attr.dob.key;
  const expiryKey = attr.docExpiryDate.key;
  return {
    ...senderDetails,
    [dobKey]: senderDetails[dobKey]
      ? formatDate(senderDetails[dobKey] as string)
      : '',
    [expiryKey]: senderDetails[expiryKey]
      ? formatDate(senderDetails[expiryKey] as string)
      : '',
  };
};
